import { useEffect, useState } from "react";
import { AlertCircle, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useTenant } from "@/lib/auth";
import { trpc, toastTrpcError } from "@/lib/trpc";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";

const planLabels: Record<string, string> = {
  free: "Free",
  pro: "Pro",
  team: "Team",
  enterprise: "Enterprise",
};

export function GeneralTab() {
  const tenantId = useTenant();
  const utils = trpc.useUtils();
  const {
    data: tenant,
    isLoading,
    error,
    refetch,
  } = trpc.tenant.getById.useQuery({
    id: tenantId,
  });

  const { data: workspaces } = trpc.tenant.listForUser.useQuery();
  const currentWorkspace =
    workspaces?.find((w) => w.isCurrent) ??
    workspaces?.find((w) => w.id === tenantId);
  const isOwner = currentWorkspace?.role === "owner";

  const [name, setName] = useState("");
  const [dirty, setDirty] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [confirmName, setConfirmName] = useState("");

  useEffect(() => {
    if (tenant?.name) {
      setName(tenant.name);
      setDirty(false);
    }
  }, [tenant?.name]);

  const updateMutation = trpc.tenant.update.useMutation({
    onSuccess: (updated) => {
      toast.success("Workspace name updated");
      utils.tenant.getById.setData({ id: tenantId }, (old) =>
        old ? { ...old, name: updated.name } : old,
      );
      utils.tenant.listForUser.invalidate();
      setDirty(false);
    },
    onError: (err) => toastTrpcError(err, "Failed to update workspace name"),
  });

  const switchMutation = trpc.profile.switchWorkspace.useMutation({
    onSuccess: () => {
      window.location.href = "/projects";
    },
    onError: () => {
      window.location.href = "/projects";
    },
  });

  const deleteMutation = trpc.tenant.delete.useMutation({
    onSuccess: async () => {
      toast.success("Workspace deleted");
      setShowDeleteDialog(false);
      const freshWorkspaces = await utils.tenant.listForUser.fetch();
      const next = freshWorkspaces.find((w) => w.id !== tenantId);
      if (next) {
        switchMutation.mutate({ tenantId: next.id });
      } else {
        window.location.href = "/projects";
      }
    },
    onError: (err) => toastTrpcError(err, "Failed to delete workspace"),
  });

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-6">
          <div className="space-y-3">
            <Skeleton className="h-5 w-40" />
            <Skeleton className="h-5 w-60" />
            <Skeleton className="h-5 w-32" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !tenant) {
    const isNotFound = error?.data?.code === "NOT_FOUND" || (!error && !tenant);
    return (
      <Card className="border-destructive/30">
        <CardContent className="flex flex-col items-center justify-center gap-3 py-8 text-center">
          <AlertCircle className="h-8 w-8 text-destructive" />
          <div className="space-y-1">
            <p className="text-sm font-medium">
              {isNotFound
                ? "Workspace not found"
                : "Failed to load workspace settings"}
            </p>
            <p className="text-xs text-muted-foreground">
              {isNotFound
                ? "This workspace does not exist or you do not have permission to view it."
                : "We encountered an error loading workspace settings. Please try again."}
            </p>
          </div>
          {!isNotFound && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => refetch()}
              className="gap-2"
            >
              <RotateCcw className="h-3.5 w-3.5" />
              Retry
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardContent className="pt-6">
          <div className="space-y-6">
            <div>
              <Label
                htmlFor="workspace-name"
                className="text-xs font-medium text-muted-foreground"
              >
                Workspace name
              </Label>
              {isOwner ? (
                <div className="mt-1.5 flex max-w-md items-center gap-2">
                  <Input
                    id="workspace-name"
                    value={name}
                    onChange={(e) => {
                      setName(e.target.value);
                      setDirty(true);
                    }}
                    className="h-9 text-sm"
                    placeholder="Workspace name"
                  />
                  <Button
                    size="sm"
                    disabled={
                      !dirty || !name.trim() || updateMutation.isPending
                    }
                    onClick={() => updateMutation.mutate({ name: name.trim() })}
                  >
                    {updateMutation.isPending ? "Saving..." : "Save"}
                  </Button>
                </div>
              ) : (
                <p className="mt-1 text-sm font-medium">{tenant.name}</p>
              )}
            </div>
            <dl className="space-y-6">
              <div>
                <dt className="text-xs font-medium text-muted-foreground">
                  Account type
                </dt>
                <dd className="mt-1 text-sm capitalize">
                  {tenant.accountType}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-muted-foreground">
                  Plan
                </dt>
                <dd className="mt-1">
                  <Badge variant="secondary" className="rounded-sm">
                    {planLabels[tenant.plan] ?? tenant.plan}
                  </Badge>
                </dd>
              </div>
            </dl>
          </div>
        </CardContent>
      </Card>

      {isOwner && (
        <Card className="border-destructive/30">
          <CardHeader>
            <CardTitle className="text-base text-destructive">
              Danger zone
            </CardTitle>
            <CardDescription className="text-xs">
              Irreversible actions for this workspace.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div>
                <p className="text-sm font-medium">Delete this workspace</p>
                <p className="text-xs text-muted-foreground">
                  Permanently remove this workspace and all associated projects,
                  sources, findings, and members.
                </p>
              </div>
              <Button
                variant="destructive"
                size="sm"
                onClick={() => {
                  setConfirmName("");
                  setShowDeleteDialog(true);
                }}
              >
                Delete workspace
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-destructive">
              Delete workspace
            </DialogTitle>
            <DialogDescription>
              This action cannot be undone. All data in{" "}
              <strong className="text-foreground">{tenant.name}</strong> will be
              permanently deleted.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label
              htmlFor="delete-confirm-name"
              className="text-xs text-muted-foreground"
            >
              To confirm, type{" "}
              <strong className="text-foreground">{tenant.name}</strong> below:
            </Label>
            <Input
              id="delete-confirm-name"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={tenant.name}
              className="h-9 text-sm"
              autoFocus
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowDeleteDialog(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              size="sm"
              disabled={
                confirmName.trim().toLowerCase() !==
                  tenant.name.trim().toLowerCase() || deleteMutation.isPending
              }
              onClick={() =>
                deleteMutation.mutate({ confirmName: confirmName.trim() })
              }
            >
              {deleteMutation.isPending ? "Deleting..." : "Permanently delete"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
